import {
  BedDouble, CheckCircle2, Database, Download, ExternalLink, FileSpreadsheet, Hotel,
  Link2, Plus, Settings2, ShieldCheck, Upload, XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { Room } from "../../shared/types";
import { Modal } from "../components/Modal";
import { Toast, type ToastMessage } from "../components/Toast";
import { api } from "../lib/api";
import { hostelworldImportTemplate, importTemplate, mapSpreadsheetCsv, type ImportRow } from "../lib/csv";

interface Integration {
  provider: string;
  status: string;
  displayName: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
}
interface PropertySettings {
  id: string;
  name: string;
  timezone: string;
  defaultCurrency: string;
  address: string | null;
  phone: string | null;
  publicEmail: string | null;
  googleReviewUrl: string | null;
}
interface ImportResult {
  imported: number;
  skipped: number;
  outcomes: Array<{ row: number; status: string; message?: string }>;
}

type ModalName = "room" | "property" | "import" | "hostelworld" | null;
type ImportMode = "all" | "hostelworld";

const providerNames: Record<string, string> = {
  airbnb: "Airbnb", expedia: "Expedia", hostelworld: "Hostelworld", google_sheets: "Google Sheets",
  email: "Email sender", whatsapp: "WhatsApp", channel_manager: "Channel manager",
};

function downloadCsv(contents: string, filename: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: "text/csv" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function connectionLabel(integration?: Integration) {
  if (!integration || integration.status === "not_connected") return "Not set up";
  if (integration.status === "pending") return "Manual pilot";
  if (integration.status === "connected") return "Live connection";
  return integration.status;
}

export function MorePage() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [property, setProperty] = useState<PropertySettings | null>(null);
  const [modal, setModal] = useState<ModalName>(null);
  const [roomType, setRoomType] = useState("private");
  const [capacity, setCapacity] = useState(1);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importMode, setImportMode] = useState<ImportMode>("all");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [nextRooms, nextIntegrations, nextProperty] = await Promise.all([
      api<Room[]>("/api/v1/rooms"), api<Integration[]>("/api/v1/integrations"), api<PropertySettings>("/api/v1/settings/property"),
    ]);
    setRooms(nextRooms);
    setIntegrations(nextIntegrations);
    setProperty(nextProperty);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const hostelworld = integrations.find((item) => item.provider === "hostelworld");
  const hostelworldPilot = hostelworld?.status === "pending";
  const hostelworldReady = hostelworldPilot || hostelworld?.status === "connected";

  function openImport(mode: ImportMode) {
    setImportMode(mode);
    setImportRows([]);
    setImportResult(null);
    setModal("import");
  }

  async function startHostelworldPilot() {
    setBusy(true);
    try {
      await api("/api/v1/integrations/hostelworld/pilot", { method: "POST", body: "{}" });
      setToast({ kind: "success", text: "Hostelworld manual pilot enabled." });
      await load();
    } catch (caught) {
      setToast({ kind: "error", text: caught instanceof Error ? caught.message : "Could not enable the Hostelworld pilot." });
    } finally { setBusy(false); }
  }

  async function addRoom(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/v1/rooms", {
        method: "POST",
        body: JSON.stringify({
          name: form.get("name"), roomType, capacity, floor: form.get("floor"),
          bedLabels: roomType === "private" ? [] : Array.from({ length: capacity }, (_, index) => String(form.get(`bed-${index}`))),
        }),
      });
      setModal(null);
      setToast({ kind: "success", text: "Room and bed inventory added." });
      await load();
    } catch (caught) {
      setToast({ kind: "error", text: caught instanceof Error ? caught.message : "Could not add room." });
    } finally { setBusy(false); }
  }

  async function saveProperty(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/v1/settings/property", {
        method: "PATCH",
        body: JSON.stringify({
          name: form.get("name"), defaultCurrency: form.get("defaultCurrency"), address: form.get("address"),
          phone: form.get("phone"), publicEmail: form.get("publicEmail"), googleReviewUrl: form.get("googleReviewUrl"),
        }),
      });
      setModal(null);
      setToast({ kind: "success", text: "Property settings updated." });
      await load();
    } catch (caught) {
      setToast({ kind: "error", text: caught instanceof Error ? caught.message : "Could not update settings." });
    } finally { setBusy(false); }
  }

  async function chooseCsv(file: File) {
    setImportResult(null);
    const rows = mapSpreadsheetCsv(await file.text());
    if (importMode === "hostelworld" && rows.some((row) => row.source !== "hostelworld")) {
      setImportRows([]);
      setToast({ kind: "error", text: "This pilot import only accepts rows with source set to hostelworld." });
      return;
    }
    setImportRows(rows);
  }

  async function runImport() {
    setBusy(true);
    try {
      const result = await api<ImportResult>("/api/v1/imports/spreadsheet", { method: "POST", body: JSON.stringify({ rows: importRows }) });
      setImportResult(result);
      setToast({ kind: result.skipped ? "error" : "success", text: `${result.imported} bookings imported; ${result.skipped} skipped.` });
      await load();
    } catch (caught) {
      setToast({ kind: "error", text: caught instanceof Error ? caught.message : "Could not import file." });
    } finally { setBusy(false); }
  }

  const template = importMode === "hostelworld" ? hostelworldImportTemplate : importTemplate;
  const templateName = importMode === "hostelworld" ? "hostelworld-booking-import-template.csv" : "hostel-booking-import-template.csv";

  return <div className="page-stack">
    <section className="hostelworld-connect-card">
      <span className="hostelworld-mark"><Hotel /></span>
      <div>
        <span className="eyebrow">First booking channel</span>
        <h2>Hostelworld</h2>
        <p>{hostelworldReady
          ? (hostelworld?.status === "connected" ? "The live connection is active. Manage Hostelworld bookings and connection details here." : "The manual pilot is active. Import Inbox bookings here while direct reservation access is being confirmed.")
          : "Set up a no-cost pilot from the owner dashboard. Your Hostelworld password stays with Hostelworld."}</p>
      </div>
      <div className="hostelworld-connect-actions">
        <span className={`status-chip ${hostelworld?.status ?? "not_connected"}`}>{connectionLabel(hostelworld)}</span>
        <button className="button button-primary" onClick={() => setModal("hostelworld")}>{hostelworldReady ? "Manage connection" : "Connect Hostelworld"}</button>
      </div>
    </section>

    <div className="settings-grid">
      <section className="panel">
        <header className="panel-header"><div><span className="eyebrow">Inventory</span><h3>Rooms & beds</h3></div><button className="button button-quiet" onClick={() => setModal("room")}><Plus size={17} />Add</button></header>
        <div className="settings-list">{rooms.map((room) => <div key={room.id}><span className="setting-icon"><BedDouble /></span><span><strong>{room.name}</strong><small>{room.roomType} · capacity {room.capacity}{room.beds.length ? ` · ${room.beds.length} beds` : ""}</small></span><span className="status-chip connected">Active</span></div>)}{!rooms.length && <div className="inline-empty">Add the property’s real rooms and beds before entering bookings.</div>}</div>
      </section>
      <section className="panel">
        <header className="panel-header"><div><span className="eyebrow">Property</span><h3>Public details</h3></div><button className="button button-quiet" onClick={() => setModal("property")}><Settings2 size={17} />Edit</button></header>
        {property && <div className="property-summary"><strong>{property.name}</strong><p>{property.address || "Address needs owner confirmation"}</p><span>{property.publicEmail || "Public email not set"}</span><span>{property.phone || "Phone not set"}</span><span>{property.googleReviewUrl ? "Google review link ready" : "Google review link needed"}</span></div>}
      </section>
    </div>

    <section className="panel">
      <header className="panel-header"><div><span className="eyebrow">Booking channels & tools</span><h3>Other connections</h3></div></header>
      <div className="integration-grid">{integrations.filter((item) => item.provider !== "hostelworld").map((item) => <article key={item.provider}><span className="setting-icon"><Link2 /></span><div><strong>{providerNames[item.provider] ?? item.provider}</strong><small>{connectionLabel(item)}</small></div><span className={`status-dot ${item.status === "connected" ? "ok" : "warning"}`} /></article>)}</div>
      <p className="panel-note">Connections require an approved provider or verified sender. Booking-site passwords are never stored here.</p>
    </section>

    <section className="data-tools">
      <article><span className="tool-icon"><Upload /></span><div><h3>Import current spreadsheet</h3><p>Preview a CSV, match existing room and bed names, then import without duplicating channel references.</p></div><button className="button button-secondary" onClick={() => openImport("all")}>Import CSV</button></article>
      <article><span className="tool-icon"><Download /></span><div><h3>Export reservations</h3><p>Download a safe Excel-compatible CSV of current booking records.</p></div><a className="button button-secondary" href="/api/v1/exports/reservations.csv">Download CSV</a></article>
      <article><span className="tool-icon"><Database /></span><div><h3>Google Sheets reporting</h3><p>One-way scheduled reporting becomes available after a Sheet and Google account are authorized.</p></div><span className="status-chip not_connected">Needs setup</span></article>
    </section>

    {modal === "hostelworld" && <Modal title="Connect Hostelworld" onClose={() => setModal(null)}>
      <div className="hostelworld-setup">
        <div className="connection-safety"><ShieldCheck /><div><strong>Your password stays with Hostelworld</strong><p>Sign in on Hostelworld’s own secure Inbox. This app never asks for or stores that password.</p></div></div>
        <ol className="connection-steps">
          <li><span>1</span><div><h3>Open the property Inbox</h3><p>Sign in to check reservations and download or copy the bookings needed for the pilot.</p><a className="button button-secondary" href="https://inbox.hostelworld.com/" target="_blank" rel="noreferrer">Open Hostelworld Inbox <ExternalLink size={15} /></a></div></li>
          <li><span>2</span><div><h3>Enable the manual pilot</h3><p>This records the setup in the dashboard. It does not claim that a live API is connected.</p>{hostelworldReady ? <span className={`status-chip ${hostelworld?.status}`}><CheckCircle2 size={13} />{hostelworld?.status === "connected" ? "Live connection" : "Pilot enabled"}</span> : <button className="button button-secondary" disabled={busy} onClick={() => void startHostelworldPilot()}>{busy ? "Enabling…" : "Enable pilot"}</button>}</div></li>
          <li><span>3</span><div><h3>Import Hostelworld bookings</h3><p>Use the Hostelworld template. Booking references are checked for duplicates and room or bed conflicts.</p>{rooms.length ? <button className="button button-primary" onClick={() => openImport("hostelworld")}>Import Hostelworld CSV</button> : <button className="button button-secondary" onClick={() => setModal("room")}>Add rooms and beds first</button>}</div></li>
        </ol>
        <p className="direct-access-note"><strong>Automatic sync:</strong> Hostelworld does not publish reservation-inbox OAuth in its public partner API. The dashboard will stay on “Manual pilot” until Hostelworld approves a direct property feed or provides a supported export.</p>
      </div>
    </Modal>}

    {modal === "room" && <Modal title="Add room inventory" onClose={() => setModal(null)}><form className="modal-form" onSubmit={addRoom}><div className="form-grid"><label className="field"><span>Room name</span><input name="name" placeholder="e.g. Dorm 2" required /></label><label className="field"><span>Floor</span><input name="floor" /></label><label className="field"><span>Room type</span><select value={roomType} onChange={(event) => setRoomType(event.target.value)}><option value="private">Private room</option><option value="dorm">Dorm beds only</option><option value="flexible">Whole room or individual beds</option></select></label><label className="field"><span>Capacity</span><input type="number" min="1" max="30" value={capacity} onChange={(event) => setCapacity(Number(event.target.value))} /></label>{roomType !== "private" && <fieldset className="bed-labels full"><legend>Bed labels</legend>{Array.from({ length: capacity }, (_, index) => <label className="field" key={index}><span>Bed {index + 1}</span><input name={`bed-${index}`} defaultValue={`Bed ${index + 1}`} required /></label>)}</fieldset>}</div><footer className="modal-actions"><button type="button" className="button button-quiet" onClick={() => setModal(null)}>Cancel</button><button className="button button-primary" disabled={busy}>Add room</button></footer></form></Modal>}

    {modal === "property" && property && <Modal title="Edit property details" onClose={() => setModal(null)}><form className="modal-form" onSubmit={saveProperty}><div className="form-grid"><label className="field full"><span>Property name</span><input name="name" defaultValue={property.name} required /></label><label className="field"><span>Default currency</span><select name="defaultCurrency" defaultValue={property.defaultCurrency}><option>PEN</option><option>USD</option><option>EUR</option></select></label><label className="field"><span>Public phone</span><input name="phone" type="tel" defaultValue={property.phone ?? ""} placeholder="+51…" /></label><label className="field full"><span>Owner-confirmed address</span><input name="address" defaultValue={property.address ?? ""} /></label><label className="field"><span>Public email</span><input name="publicEmail" type="email" defaultValue={property.publicEmail ?? ""} /></label><label className="field full"><span>Google review URL</span><input name="googleReviewUrl" type="url" defaultValue={property.googleReviewUrl ?? ""} /></label></div><footer className="modal-actions"><button type="button" className="button button-quiet" onClick={() => setModal(null)}>Cancel</button><button className="button button-primary" disabled={busy}>Save changes</button></footer></form></Modal>}

    {modal === "import" && <Modal title={importMode === "hostelworld" ? "Import Hostelworld bookings" : "Import spreadsheet bookings"} onClose={() => setModal(null)}><div className="modal-form"><div className="import-instructions"><FileSpreadsheet /><div><h3>Use the {importMode === "hostelworld" ? "Hostelworld " : ""}import template</h3><p>Room and bed labels must exactly match inventory. Existing booking references are skipped. Contact details do not grant message permission.</p></div><button className="text-button" onClick={() => downloadCsv(template, templateName)}>Download template</button></div><label className="file-drop"><Upload /><strong>Choose a CSV file</strong><span>Up to 500 rows at a time</span><input type="file" accept=".csv,text/csv" onChange={(event) => { const file = event.target.files?.[0]; if (file) void chooseCsv(file); }} /></label>{importRows.length > 0 && <div className="import-preview"><h3>{importRows.length} rows ready to review</h3><div className="table-wrap"><table><thead><tr><th>Guest</th><th>Dates</th><th>Room / bed</th><th>Source</th></tr></thead><tbody>{importRows.slice(0, 5).map((row, index) => <tr key={index}><td>{row.guestName || <em>Missing</em>}</td><td>{row.checkInDate} → {row.checkOutDate}</td><td>{row.roomName}{row.bedLabel ? ` / ${row.bedLabel}` : ""}</td><td>{row.source}</td></tr>)}</tbody></table></div>{importRows.length > 5 && <small>Plus {importRows.length - 5} more rows</small>}</div>}{importResult && <div className="import-results"><strong>{importResult.imported} imported · {importResult.skipped} skipped</strong>{importResult.outcomes.filter((item) => item.status === "skipped").map((item) => <p key={item.row}><XCircle /> Row {item.row}: {item.message}</p>)}{importResult.skipped === 0 && <p><CheckCircle2 />All rows imported successfully.</p>}</div>}<footer className="modal-actions"><button className="button button-quiet" onClick={() => setModal(null)}>Close</button>{importRows.length > 0 && !importResult && <button className="button button-primary" disabled={busy} onClick={() => void runImport()}>{busy ? "Importing…" : `Import ${importRows.length} bookings`}</button>}</footer></div></Modal>}

    <Toast message={toast} onClose={() => setToast(null)} />
  </div>;
}
